import { createAsyncThunk } from '@reduxjs/toolkit';
import { WellframeApi } from 'store/api';
import {
  LS_ITEM_ACCESS_TOKEN,
  LS_ITEM_OIDC_TOKEN,
  LS_ITEM_REFRESH_TOKEN,
  LS_ITEM_IS_LOGGED_IN,
  LS_IS_CAREGIVER_WAITING_FOR_APPROVAL,
} from 'store/constants';
import { deleteCookieAcrossDomains } from 'utils/cookieUtils';
import { clearPendoSession } from 'features/pendo/utils/pendoUtils';
import { clearAuth } from '../authSlice';

/**
 * Clear Qualtrics cookies with multiple domain variations
 */
const clearQualtricsCookies = (): void => {
  try {
    const cookies = document.cookie.split(';');
    const qualtricsPrefixes = ['QSI_', 'QSI-', 'QS_', 'Q_', 'SIATE_', 'SIE_'];
    const qualtricsDomains = [
      '',
      window.location.hostname,
      `.${window.location.hostname}`,
      '.qualtrics.com',
      '.siteintercept.qualtrics.com',
      'qualtrics.com',
      'siteintercept.qualtrics.com',
    ];

    cookies.forEach((cookie) => {
      const equalSignIndex = cookie.indexOf('=');
      const name = equalSignIndex > -1 ? cookie.substring(0, equalSignIndex).trim() : cookie.trim();

      const isQualtricsCookie = qualtricsPrefixes.some((prefix) => name.startsWith(prefix));

      if (isQualtricsCookie) {
        deleteCookieAcrossDomains(name, qualtricsDomains, ['/', '/;']);
      }
    });
  } catch (e) {
    console.log('Error clearing Qualtrics cookies');
  }
};

/**
 * Clear Qualtrics data from localStorage
 */
const clearQualtricsLocalStorage = (): void => {
  try {
    const localStorageKeys = Object.keys(localStorage);
    const qualtricsKeywords = ['QSI', 'qualtrics', 'Qualtrics', 'SIATE'];

    localStorageKeys.forEach((key) => {
      const isQualtricsKey = qualtricsKeywords.some((keyword) => key.includes(keyword));
      if (isQualtricsKey) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.log('Error clearing Qualtrics localStorage');
  }
};

/**
 * Clear Qualtrics data from sessionStorage
 */
const clearQualtricsSessionStorage = (): void => {
  try {
    const sessionStorageKeys = Object.keys(sessionStorage);
    const qualtricsKeywords = ['QSI', 'qualtrics', 'Qualtrics', 'SIATE'];

    sessionStorageKeys.forEach((key) => {
      const isQualtricsKey = qualtricsKeywords.some((keyword) => key.includes(keyword));
      if (isQualtricsKey) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear our custom session parameters
    const customQualtricsKeys = [
      'qualtrics_reset_timestamp',
      'qualtrics_challenge',
      'qualtrics_time',
      'qualtrics_last_intercept_id',
    ];

    customQualtricsKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch (e) {
    console.log('Error clearing Qualtrics sessionStorage');
  }
};

/**
 * Remove Qualtrics DOM elements and scripts
 */
const clearQualtricsDOMElements = (): void => {
  try {
    // Remove Qualtrics scripts
    const scriptSelectors = [
      'script[src*="qualtrics.com"]',
      'script[src*="siteintercept"]',
      'script[id*="qualtrics"]',
      'script[id*="QSI"]',
    ];

    scriptSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((script) => {
        script.remove();
      });
    });

    // Remove Qualtrics DOM elements
    const elementSelectors = [
      '.QSIWebResponsive',
      '[id*="QSI"]',
      '[class*="QSI"]',
      '[class*="qualtrics"]',
    ];

    elementSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.remove();
      });
    });
  } catch (e) {
    console.log('Error clearing Qualtrics DOM elements');
  }
};

/**
 * Clear global Qualtrics state and API
 */
const clearQualtricsGlobalState = (): void => {
  try {
    if (window.QSI) {
      try {
        window.QSI.API?.unload();
      } catch (e) {
        console.log('Error unloading QSI API');
      }
      delete window.QSI;
    }
  } catch (e) {
    console.log('Error clearing global Qualtrics state');
  }
};

/**
 * Clear Qualtrics IndexedDB databases
 */
const clearQualtricsIndexedDB = (): void => {
  try {
    if ('indexedDB' in window && indexedDB.databases) {
      indexedDB
        .databases()
        .then((databases) => {
          const qualtricsKeywords = ['QSI', 'qualtrics', 'Qualtrics', 'SIATE'];

          databases.forEach((db) => {
            const { name: databaseName } = db;
            if (databaseName) {
              const isQualtricsDB = qualtricsKeywords.some((keyword) =>
                databaseName.includes(keyword),
              );

              if (isQualtricsDB) {
                indexedDB.deleteDatabase(databaseName);
              }
            }
          });
        })
        .catch((e) => {
          console.log('Error listing IndexedDB databases');
        });
    }
  } catch (e) {
    console.log('Error clearing Qualtrics IndexedDB');
  }
};

/**
 * Comprehensive Qualtrics cleanup function
 * Clears cookies, localStorage, sessionStorage, DOM elements, and global state
 */
const clearAllQualtricsData = (): void => {
  clearQualtricsCookies();
  clearQualtricsLocalStorage();
  clearQualtricsSessionStorage();
  clearQualtricsDOMElements();
  clearQualtricsGlobalState();
  clearQualtricsIndexedDB();
};

export const logoutThunk = createAsyncThunk('auth/logoutThunk', async (_, { dispatch }) => {
  // regardless of whether Qualtrics data is present, we clear all Qualtrics data
  // to ensure a clean state for the next session
  clearAllQualtricsData();
  // Clear Pendo session
  clearPendoSession();
  // Clear auth tokens
  localStorage.removeItem(LS_ITEM_ACCESS_TOKEN);
  localStorage.removeItem(LS_ITEM_REFRESH_TOKEN);
  localStorage.removeItem(LS_ITEM_OIDC_TOKEN);
  localStorage.removeItem(LS_ITEM_IS_LOGGED_IN);
  localStorage.removeItem(LS_IS_CAREGIVER_WAITING_FOR_APPROVAL);
  // When we clear auth app navigates to login page unmounting any components
  // that are still subscribed to RTK Query hooks. This cancels any ongoing query subscriptions
  // preventing unnecessary refetches of queries when resetApiState() is called inside logoutThunk
  dispatch(clearAuth());
  dispatch(WellframeApi.util.resetApiState());
});
